import React, { useState, useEffect } from 'react';
import type { AnimalWithStats, Feeding, Weight } from '../data/types';
import { db } from '../data/storage';
import { formatTimeAgo } from '../utils/timeFormat';
import './AnimalDetailView.css';

interface AnimalDetailViewProps {
    animal: AnimalWithStats | null;
    onClose: () => void;
    onEdit: (animal: AnimalWithStats) => void;
    onArchive: (animal: AnimalWithStats) => void;
    onDelete: (animal: AnimalWithStats) => void;
    formatWeight: (grams: number) => string;
}

export function AnimalDetailView({
                                     animal,
                                     onClose,
                                     onEdit,
                                     onArchive,
                                     onDelete,
                                     formatWeight
                                 }: AnimalDetailViewProps) {
    const [feedings, setFeedings] = useState<Feeding[]>([]);
    const [weights, setWeights] = useState<Weight[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            if (animal) {
                const [feedingsData, weightsData] = await Promise.all([
                    db.getFeedings(animal.id),
                    db.getWeights(animal.id)
                ]);
                setFeedings(feedingsData);
                setWeights(weightsData);
            }
        };
        loadData();
    }, [animal]);

    if (!animal) return null;

    const handleExport = () => {
        const exportData = {
            animal: {
                name: animal.name,
                notes: animal.notes,
                feedingIntervalHours: animal.feedingIntervalHours,
                createdAt: new Date(animal.createdAt).toISOString(),
            },
            feedings: feedings.map(f => ({
                timestamp: new Date(f.timestamp).toISOString(),
                amountMl: f.amountMl,
                urinated: f.urinated,
                pooped: f.pooped,
                poopConsistency: f.poopConsistency,
                poopColor: f.poopColor,
                notes: f.notes,
            })),
            weights: weights.map(w => ({
                timestamp: new Date(w.timestamp).toISOString(),
                weightGrams: w.weightGrams,
                notes: w.notes,
            })),
            exportedAt: new Date().toISOString(),
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `foster-pal-${animal.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleArchive = () => {
        if (confirm(`Archive ${animal.name}? They'll be moved to archived animals but data will be preserved.`)) {
            onArchive(animal);
            onClose();
        }
    };

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        onDelete(animal);
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="detail-view active" onClick={handleBackdropClick}>
            <div className="detail-content">
                <div className="detail-header">
                    <button className="back-btn" onClick={onClose}>←</button>
                    <h2>{animal.name}</h2>
                    <div style={{ width: '40px' }} />
                </div>

                <div className="detail-body">
                    <div className="detail-section">
                        <div className="section-title">Animal Info</div>
                        <div className="info-grid">
                            <div className="info-item">
                                <div className="info-label">Feeding Interval</div>
                                <div className="info-value">Every {animal.feedingIntervalHours}h</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">Total Feedings</div>
                                <div className="info-value">{feedings.length}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">Total Weigh-ins</div>
                                <div className="info-value">{weights.length}</div>
                            </div>
                            {animal.notes && (
                                <div className="info-item full-width">
                                    <div className="info-label">Notes</div>
                                    <div className="info-value">{animal.notes}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="detail-section">
                        <div className="section-title">Recent Activity</div>

                        {feedings.length === 0 && weights.length === 0 ? (
                            <div className="empty-message">No activity yet</div>
                        ) : (
                            <div className="activity-list">
                                {[...feedings.map(f => ({ type: 'feeding' as const, data: f, timestamp: f.timestamp })),
                                    ...weights.map(w => ({ type: 'weight' as const, data: w, timestamp: w.timestamp }))]
                                    .sort((a, b) => b.timestamp - a.timestamp)
                                    .slice(0, 10)
                                    .map((item, idx) => (
                                        <div key={idx} className="activity-item">
                                            <div className="activity-icon">
                                                {item.type === 'feeding' ? '🍼' : '⚖️'}
                                            </div>
                                            <div className="activity-details">
                                                <div className="activity-type">
                                                    {item.type === 'feeding' ? 'Feeding' : 'Weight Check'}
                                                </div>
                                                <div className="activity-info">
                                                    {item.type === 'feeding'
                                                        ? `${(item.data as Feeding).amountMl}mL`
                                                        : formatWeight((item.data as Weight).weightGrams)
                                                    }
                                                    {item.type === 'feeding' && (item.data as Feeding).pooped && ' • 💩'}
                                                    {item.type === 'feeding' && (item.data as Feeding).urinated && ' • 💧'}
                                                </div>
                                            </div>
                                            <div className="activity-time">
                                                {formatTimeAgo(item.timestamp)}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>

                    <div className="detail-section">
                        <div className="section-title">Actions</div>
                        <div className="action-buttons">
                            <button className="action-btn edit" onClick={() => onEdit(animal)}>
                                ✏️ Edit {animal.name}
                            </button>
                            <button className="action-btn export" onClick={handleExport}>
                                📤 Export Data
                            </button>
                            <button className="action-btn archive" onClick={handleArchive}>
                                📦 Archive {animal.name}
                            </button>
                            <button className="action-btn delete" onClick={handleDelete}>
                                🗑️ Delete {animal.name}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showDeleteConfirm && (
                <div className="confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Delete {animal.name}?</h3>
                        <p>This will permanently delete all feeding and weight records. This cannot be undone.</p>
                        <p><strong>Consider archiving instead to preserve records.</strong></p>
                        <div className="confirm-actions">
                            <button className="confirm-cancel" onClick={() => setShowDeleteConfirm(false)}>
                                Cancel
                            </button>
                            <button className="confirm-delete" onClick={confirmDelete}>
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}